import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, X, Trash2, Minus, Plus, ShoppingCart, CreditCard, Truck, TicketPercent } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import TrustBadges from "@/components/TrustBadges";

const inputCls =
  "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

const FORM_ID = "cart-checkout-form";

export default function CartDrawer() {
  const { items, isOpen, closeCart, remove, setQty, total, count } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_address: "" });
  const [loading, setLoading] = useState(false);
  const [shippingModes, setShippingModes] = useState([]);
  const [shippingMode, setShippingMode] = useState("");
  const [freeFrom, setFreeFrom] = useState(0);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen || items.length === 0) setStep("cart");
  }, [isOpen, items.length]);

  useEffect(() => {
    if (isOpen) {
      api.get("/shipping").then((r) => {
        setShippingModes(r.data.modes || []);
        setShippingMode((r.data.modes || [])[0]?.key || "");
        setFreeFrom(r.data.free_from || 0);
      }).catch(() => {});
      if (user && user.role !== "admin") {
        setForm((f) => ({
          customer_name: f.customer_name || user.profile_name || user.name || "",
          customer_email: f.customer_email || user.email || "",
          customer_address: f.customer_address || user.profile_address || "",
        }));
      }
    }
  }, [isOpen, user]);

  const applyPromo = async () => {
    try {
      const { data } = await api.post("/promos/validate", { code: promoInput });
      setPromo(data);
      toast.success(`Code ${data.code} appliqué : -${data.percent} %`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const shipping = shippingModes.find((m) => m.key === shippingMode);
  const discount = promo ? Math.round(total * promo.percent) / 100 : 0;
  const subAfterPromo = total - discount;
  const shippingFree = freeFrom > 0 && subAfterPromo >= freeFrom;
  const shippingPrice = shipping && !shippingFree ? shipping.price : 0;
  const grandTotal = subAfterPromo + shippingPrice;

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/cart-checkout", {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
          flocage: i.flocage,
          pseudo: i.pseudo,
        })),
        ...form,
        shipping_mode: shippingMode,
        promo_code: promo?.code || "",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-[100dvh] w-full max-w-md flex-col border-l border-orange-500/20 bg-[#0B132B]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-orange-500/20 px-4 py-3 sm:px-6">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase">
                {step === "details" ? (
                  <button type="button" onClick={() => setStep("cart")} data-testid="cart-back-button" aria-label="Retour au panier"
                    className="-ml-2 flex h-11 w-11 items-center justify-center text-slate-400 transition-colors hover:text-orange-400">
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                ) : (
                  <ShoppingCart className="h-5 w-5 text-orange-500" />
                )}
                {step === "details" ? "Livraison" : `Votre panier${count > 0 ? ` (${count})` : ""}`}
              </h2>
              <button onClick={closeCart} data-testid="cart-close-button" aria-label="Fermer le panier"
                className="-mr-2 flex h-11 w-11 items-center justify-center text-slate-400 transition-colors hover:text-orange-400">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6" data-lenis-prevent data-testid="cart-scroll-area">
              {items.length === 0 ? (
                <p className="py-12 text-center text-sm text-slate-400" data-testid="cart-empty">
                  Votre panier est vide. Rendez-vous dans la boutique pour choisir votre maillot !
                </p>
              ) : step === "cart" ? (
                <div className="grid gap-4">
                  {items.map((i, idx) => (
                    <div key={i.key} data-testid={`cart-item-${idx}`}
                      className="flex gap-3 rounded-xl border border-slate-700/60 bg-[#060B18] p-3">
                      {i.image_url && (
                        <img src={i.image_url} alt={i.name} className="h-20 w-20 shrink-0 rounded-lg object-contain" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold uppercase leading-tight">{i.name}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          Taille {i.size}
                          {i.flocage && i.pseudo ? ` — Flocage : ${i.pseudo}` : ""}
                        </p>
                        <p className="mt-1 font-mono2 text-sm font-bold text-orange-500">
                          {((i.price + (i.flocage ? i.flocage_price : 0)) * i.quantity).toFixed(2)} €
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <button type="button" onClick={() => setQty(i.key, i.quantity - 1)} disabled={i.quantity <= 1}
                            data-testid={`cart-qty-minus-${idx}`} aria-label="Réduire la quantité"
                            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-600 text-slate-300 transition-colors hover:border-orange-500 disabled:opacity-30">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center text-sm font-bold" data-testid={`cart-qty-${idx}`}>{i.quantity}</span>
                          <button type="button" onClick={() => setQty(i.key, i.quantity + 1)} disabled={i.quantity >= 20}
                            data-testid={`cart-qty-plus-${idx}`} aria-label="Augmenter la quantité"
                            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-600 text-slate-300 transition-colors hover:border-orange-500 disabled:opacity-30">
                            <Plus className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => remove(i.key)} data-testid={`cart-remove-${idx}`}
                            aria-label="Retirer l'article"
                            className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-red-500/40 text-red-400 transition-colors hover:bg-red-500/10">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input placeholder="Code promo" value={promoInput} data-testid="cart-promo-input"
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      className={inputCls} disabled={!!promo} />
                    {promo ? (
                      <button type="button" onClick={() => { setPromo(null); setPromoInput(""); }} data-testid="cart-promo-remove"
                        className="shrink-0 rounded-lg border border-red-500/40 px-3 text-xs font-bold uppercase text-red-400 hover:bg-red-500/10">
                        Retirer
                      </button>
                    ) : (
                      <button type="button" onClick={applyPromo} disabled={!promoInput.trim()} data-testid="cart-promo-apply"
                        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-orange-500/40 px-3 text-xs font-bold uppercase text-orange-400 transition-colors hover:bg-orange-500/10 disabled:opacity-40">
                        <TicketPercent className="h-4 w-4" /> Appliquer
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <form id={FORM_ID} onSubmit={checkout} className="grid gap-3">
                  <input placeholder="Nom complet" value={form.customer_name} required
                    data-testid="cart-name-input" onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className={inputCls} />
                  <input type="email" placeholder="Email" value={form.customer_email} required
                    data-testid="cart-email-input" onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    className={inputCls} />
                  <textarea placeholder="Adresse de livraison" value={form.customer_address} required rows={3}
                    data-testid="cart-address-input" onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                    className={inputCls} />
                  {shippingModes.length > 0 && (
                    <div className="grid gap-2" data-testid="cart-shipping-modes">
                      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                        <Truck className="h-4 w-4 text-orange-500" /> Mode de livraison
                      </p>
                      {freeFrom > 0 && (
                        shippingFree ? (
                          <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-400" data-testid="cart-shipping-free">
                            Livraison offerte sur votre commande !
                          </p>
                        ) : (
                          <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2" data-testid="cart-shipping-progress">
                            <p className="text-xs font-bold text-orange-400">
                              Plus que {(freeFrom - subAfterPromo).toFixed(2)} € d'articles pour la livraison offerte
                            </p>
                            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-700">
                              <div className="h-full rounded-full bg-orange-500 transition-all duration-500"
                                style={{ width: `${Math.min(100, (subAfterPromo / freeFrom) * 100)}%` }} />
                            </div>
                          </div>
                        )
                      )}
                      {shippingModes.map((m) => (
                        <label key={m.key} data-testid={`cart-shipping-${m.key}`}
                          className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 text-sm transition-colors ${
                            shippingMode === m.key ? "border-orange-500 bg-orange-500/10" : "border-slate-700 hover:border-orange-500/40"
                          }`}>
                          <span className="flex items-center gap-3">
                            <input type="radio" name="shipping" checked={shippingMode === m.key} onChange={() => setShippingMode(m.key)}
                              className="h-4 w-4 accent-orange-500" />
                            <span>
                              <span className="font-bold text-slate-200">{m.label}</span>
                              {m.delay && <span className="block text-[11px] text-slate-500">{m.delay}</span>}
                            </span>
                          </span>
                          <span className="font-mono2 font-bold text-orange-400">
                            {shippingFree ? (
                              <><span className="mr-1 text-slate-500 line-through">{m.price.toFixed(2)} €</span>Offerte</>
                            ) : (m.price > 0 ? `${m.price.toFixed(2)} €` : "Offerte")}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  <TrustBadges />
                </form>
              )}
            </div>

            {items.length > 0 && (
              <div className="grid gap-3 border-t border-orange-500/20 px-4 py-3 sm:px-6" data-testid="cart-footer">
                <div className="grid gap-1 text-sm">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Sous-total articles</span>
                    <span className="font-mono2">{total.toFixed(2)} €</span>
                  </div>
                  {promo && (
                    <div className="flex items-center justify-between text-emerald-400" data-testid="cart-promo-discount">
                      <span>Code {promo.code} (-{promo.percent} %)</span>
                      <span className="font-mono2">-{discount.toFixed(2)} €</span>
                    </div>
                  )}
                  {step === "details" && (
                    <>
                      <div className="flex items-center justify-between text-slate-400">
                        <span>Livraison{shipping ? ` — ${shipping.label}` : ""}</span>
                        <span className="font-mono2" data-testid="cart-shipping-price">{shippingPrice > 0 ? `${shippingPrice.toFixed(2)} €` : "Offerte"}</span>
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-700 pt-2">
                        <span className="font-bold uppercase tracking-widest text-slate-400">Total</span>
                        <span className="font-mono2 text-xl font-bold text-orange-500" data-testid="cart-total">
                          {grandTotal.toFixed(2)} €
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {step === "cart" ? (
                  <button type="button" onClick={() => setStep("details")} data-testid="cart-continue-button"
                    className="glow-orange flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98]">
                    Continuer vers la livraison
                  </button>
                ) : (
                  <button type="submit" form={FORM_ID} disabled={loading} data-testid="cart-checkout-button"
                    className="glow-orange flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:opacity-50">
                    <CreditCard className="h-5 w-5" />
                    {loading ? "Redirection vers le paiement..." : `Payer — ${grandTotal.toFixed(2)} €`}
                  </button>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
